// User preferences types based on Trading API v1.1.0 schema

export interface UserPreferences {
  userId: string;
  trading: TradingPreferences;
  ui: UIPreferences;
  chart: ChartPreferences;
  notifications: NotificationPreferences;
  watchlists: WatchlistPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface TradingPreferences {
  defaultStake: number;
  defaultCurrency: string;
  defaultDuration: string;
  defaultDurationType: 'minutes' | 'hours' | 'days';
  preferredProducts: string[];
  riskLevel: 'conservative' | 'moderate' | 'aggressive';
  autoClosePositions: boolean;
  confirmationRequired: boolean;
}

export interface UIPreferences {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  timezone: string;
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  currency: string;
  compactMode: boolean;
  showTooltips: boolean;
  animationsEnabled: boolean;
}

export interface ChartPreferences {
  defaultType: 'candlestick' | 'line';
  defaultTimeframe: '1s' | '1m' | '5m' | '15m' | '1h' | '1d';
  showVolume: boolean;
  showGrid: boolean;
  colorScheme: 'light' | 'dark' | 'auto';
  indicators: ChartIndicator[];
  savedTemplates: ChartTemplate[];
}

export interface ChartIndicator {
  id: string;
  name: string;
  type: 'sma' | 'ema' | 'rsi' | 'macd' | 'bollinger';
  parameters: { [key: string]: any };
  enabled: boolean;
}

export interface ChartTemplate {
  id: string;
  name: string;
  chartType: 'candlestick' | 'line';
  timeframe: string;
  indicators: ChartIndicator[];
  settings: { [key: string]: any };
  createdAt: string;
}

export interface NotificationPreferences {
  email: boolean;
  push: boolean;
  positionExpiry: boolean;
  priceAlerts: boolean;
  tradeResults: boolean;
  balanceUpdates: boolean;
  news: boolean;
  systemAlerts: boolean;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
}

export interface WatchlistPreferences {
  defaultWatchlist: string;
  autoUpdate: boolean;
  showPriceChanges: boolean;
  sortBy: 'name' | 'price' | 'change' | 'volume';
  sortOrder: 'asc' | 'desc';
}

// Watchlist types
export interface Watchlist {
  id: string;
  name: string;
  description?: string;
  instruments: WatchlistInstrument[];
  isDefault: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WatchlistInstrument {
  instrumentId: string;
  symbol: string;
  name: string;
  addedAt: string;
  notes?: string;
  alertPrice?: number;
  alertCondition?: 'above' | 'below';
}

export interface CreateWatchlistRequest {
  name: string;
  description?: string;
  instruments?: string[];
  isDefault?: boolean;
  isPublic?: boolean;
}

export interface UpdateWatchlistRequest {
  name?: string;
  description?: string;
  isDefault?: boolean;
  isPublic?: boolean;
}

export interface AddToWatchlistRequest {
  instrumentId: string;
  notes?: string;
  alertPrice?: number;
  alertCondition?: 'above' | 'below';
}

// API response types
export interface PreferencesResponse {
  preferences: UserPreferences;
  success: boolean;
}

export interface WatchlistResponse {
  watchlists: Watchlist[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface UpdatePreferencesRequest {
  trading?: Partial<TradingPreferences>;
  ui?: Partial<UIPreferences>;
  chart?: Partial<ChartPreferences>;
  notifications?: Partial<NotificationPreferences>;
  watchlists?: Partial<WatchlistPreferences>;
}

// Default preferences
export const DEFAULT_PREFERENCES: Omit<UserPreferences, 'userId' | 'createdAt' | 'updatedAt'> = {
  trading: {
    defaultStake: 10,
    defaultCurrency: 'USD',
    defaultDuration: '5',
    defaultDurationType: 'minutes',
    preferredProducts: ['rise_fall'],
    riskLevel: 'moderate',
    autoClosePositions: false,
    confirmationRequired: true
  },
  ui: {
    theme: 'dark',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    currency: 'USD',
    compactMode: false,
    showTooltips: true,
    animationsEnabled: true
  },
  chart: {
    defaultType: 'candlestick',
    defaultTimeframe: '5m',
    showVolume: true,
    showGrid: true,
    colorScheme: 'dark',
    indicators: [],
    savedTemplates: []
  },
  notifications: {
    email: true,
    push: true,
    positionExpiry: true,
    priceAlerts: true,
    tradeResults: true,
    balanceUpdates: true,
    news: false,
    systemAlerts: true,
    soundEnabled: true,
    vibrationEnabled: false
  },
  watchlists: {
    defaultWatchlist: '',
    autoUpdate: true,
    showPriceChanges: true,
    sortBy: 'name',
    sortOrder: 'asc'
  }
};
